import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Instagram, Check, Plus, Coins, Trash2, ArrowUpRight, Bell, Heart, CreditCard, CheckCircle2, XCircle } from "lucide-react";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SiteLayout } from "@/components/site/SiteLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CATS = ["Mid Fade", "High Fade", "Low Fade", "Skin Fade", "Beard", "Line Up", "Scissor Work", "Taper", "Styling"];

export default function Community() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [params, setParams] = useSearchParams();
  const loadMe = useCallback(() => user && api.get("/community/me").then(({ data }) => setMe(data)).catch(() => {}), [user]);
  useEffect(() => { document.title = "Community — Epic Fade Academy"; loadMe(); }, [loadMe]);

  useEffect(() => {
    const sub = params.get("sub");
    if (!sub) return;
    if (sub === "success") { toast.success("Membership activated! Welcome to the community."); loadMe(); }
    if (sub === "cancel") toast.info("Checkout cancelled — you were not charged.");
    params.delete("sub"); params.delete("session_id"); setParams(params, { replace: true });
  }, [params, setParams, loadMe]);

  if (!user) {
    return <SiteLayout><div className="mx-auto max-w-lg px-6 py-32 text-center">
      <Coins className="mx-auto h-12 w-12 text-[#DCA846]" />
      <h1 className="mt-6 font-display text-4xl font-bold uppercase">Barber Support Community</h1>
      <p className="mt-3 text-zinc-400">Support other barbers on Instagram, earn credits, and get your own work seen.</p>
      <Button data-testid="community-join-cta" onClick={() => (window.location.href = "/register")} className="mt-6 bg-[#DCA846] text-black hover:bg-[#C5963B] font-display uppercase">Join The Community</Button>
    </div></SiteLayout>;
  }

  const unread = me?.unread_notifications || 0;

  return (
    <SiteLayout>
      <div className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl font-bold uppercase tracking-tighter">Community</h1>
            <p className="mt-1 text-zinc-400">Support other barbers to earn credits. Credits get your work seen.</p>
          </div>
          <div data-testid="credit-balance" className="flex items-center gap-2 rounded-xl border border-[#DCA846]/30 bg-[#DCA846]/10 px-5 py-3">
            <Coins className="h-5 w-5 text-[#DCA846]" />
            <span className="font-display text-2xl text-[#DCA846]">{me?.profile?.credits ?? "—"}</span>
            <span className="text-xs uppercase text-zinc-400">Credits</span>
          </div>
        </div>

        <Tabs defaultValue="queue" className="mt-8">
          <TabsList className="flex-wrap bg-zinc-900 border border-white/10">
            <TabsTrigger value="queue" data-testid="tab-queue">Support Queue</TabsTrigger>
            <TabsTrigger value="submit" data-testid="tab-submit">Submit Post</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity" className="relative">
              Activity
              {unread > 0 && <span data-testid="notif-badge" className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#DCA846] px-1 text-[10px] font-bold text-black">{unread}</span>}
            </TabsTrigger>
            <TabsTrigger value="membership" data-testid="tab-membership">Membership</TabsTrigger>
            <TabsTrigger value="mine" data-testid="tab-mine">My Credits</TabsTrigger>
          </TabsList>
          <TabsContent value="queue" className="mt-6"><SupportQueue onSupported={loadMe} /></TabsContent>
          <TabsContent value="submit" className="mt-6"><Submit me={me} onDone={loadMe} /></TabsContent>
          <TabsContent value="activity" className="mt-6"><Activity onRead={loadMe} /></TabsContent>
          <TabsContent value="membership" className="mt-6"><Membership me={me} onChange={loadMe} /></TabsContent>
          <TabsContent value="mine" className="mt-6"><MyCredits /></TabsContent>
        </Tabs>
      </div>
    </SiteLayout>
  );
}

function SupportQueue({ onSupported }) {
  const [items, setItems] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState({});

  const fetchBatch = async (from) => {
    setLoading(true);
    const { data } = await api.get("/community/queue", { params: { skip: from, limit: 15 } });
    setItems((prev) => from === 0 ? data.items : [...prev, ...data.items]);
    setHasMore(data.has_more); setSkip(from + data.limit); setLoading(false);
  };
  useEffect(() => { fetchBatch(0); }, []);

  const openIg = async (p) => {
    try { const { data } = await api.post(`/community/support/${p.id}/open`); window.open(data.instagram_url, "_blank"); setOpened((o) => ({ ...o, [p.id]: true })); }
    catch (e) { toast.error("Could not open"); }
  };
  const confirm = async (p) => {
    try {
      const { data } = await api.post(`/community/support/${p.id}/confirm`);
      toast.success(`+${data.gained} credits! Balance: ${data.credits}`);
      setItems((prev) => prev.filter((x) => x.id !== p.id));
      onSupported();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not confirm"); }
  };

  if (loading && items.length === 0) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  if (items.length === 0) return <p className="py-16 text-center text-zinc-500">No posts to support right now. Check back soon!</p>;

  return (
    <div>
      <div className="grid gap-4 sm:grid-cols-2">
        {items.map((p) => (
          <div key={p.id} data-testid={`queue-post-${p.id}`} className="flex gap-4 rounded-xl border border-white/10 bg-zinc-900/50 p-4">
            {p.thumbnail ? <img src={resolveImg(p.thumbnail)} className="h-20 w-20 shrink-0 rounded-lg object-cover" alt="" />
              : <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-lg bg-zinc-800"><Instagram className="h-6 w-6 text-[#DCA846]" /></div>}
            <div className="min-w-0 flex-1">
              <p className="font-display uppercase text-white truncate">{p.author_name}</p>
              <p className="text-xs text-zinc-500">@{p.instagram_username || p.author_username} · {p.category}</p>
              {p.caption && <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{p.caption}</p>}
              <div className="mt-3 flex gap-2">
                <Button size="sm" data-testid={`open-${p.id}`} onClick={() => openIg(p)} className="bg-gradient-to-r from-[#DCA846] to-[#C5963B] text-black hover:opacity-90">
                  <Instagram className="mr-1 h-4 w-4" /> Support on IG <ArrowUpRight className="ml-1 h-3 w-3" />
                </Button>
                <Button size="sm" data-testid={`confirm-${p.id}`} variant="outline" disabled={!opened[p.id]} onClick={() => confirm(p)}
                  className="border-emerald-500/40 text-emerald-400 disabled:opacity-40">
                  <Check className="mr-1 h-4 w-4" /> Confirm +3
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {hasMore && <div className="mt-8 text-center"><Button data-testid="load-more" onClick={() => fetchBatch(skip)} variant="outline" className="border-white/15 text-white">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Load More"}</Button></div>}
    </div>
  );
}

function Submit({ me, onDone }) {
  const [form, setForm] = useState({ instagram_url: "", caption: "", category: "Mid Fade", instagram_username: "" });
  const [posts, setPosts] = useState([]);
  const load = () => api.get("/community/my-posts").then(({ data }) => setPosts(data));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try { await api.post("/community/submit", form); toast.success("Post submitted!"); setForm({ ...form, instagram_url: "", caption: "" }); load(); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/community/posts/${id}`); load(); };
  return (
    <div className="grid gap-8 md:grid-cols-2">
      <div className="space-y-3 rounded-xl border border-white/10 bg-zinc-900/40 p-5">
        <p className="font-display uppercase text-[#DCA846]">Submit an Instagram post</p>
        <Input data-testid="ig-url" placeholder="https://instagram.com/p/..." value={form.instagram_url} onChange={(e) => setForm({ ...form, instagram_url: e.target.value })} className="bg-zinc-900 border-white/10 text-white" />
        <Input placeholder="Your IG @username" value={form.instagram_username} onChange={(e) => setForm({ ...form, instagram_username: e.target.value })} className="bg-zinc-900 border-white/10 text-white" />
        <Textarea placeholder="Short caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} className="bg-zinc-900 border-white/10 text-white" />
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
          <SelectTrigger className="bg-zinc-900 border-white/10 text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10 text-white">{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Button data-testid="submit-post-btn" onClick={submit} className="w-full bg-[#DCA846] text-black hover:bg-[#C5963B] font-display uppercase"><Plus className="mr-1 h-4 w-4" /> Submit</Button>
        <p className="text-xs text-zinc-500">Your posts receive supports while you have credits ({me?.profile?.credits ?? 0} left). Earn +3 credits per barber you support.</p>
      </div>
      <div>
        <p className="mb-3 font-display uppercase text-zinc-300">My submitted posts</p>
        <div className="space-y-2">
          {posts.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-zinc-900/40 px-4 py-2 text-sm">
              <span className="truncate text-zinc-300">{p.category} · {p.support_count} supports</span>
              <button onClick={() => del(p.id)} className="text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {posts.length === 0 && <p className="text-zinc-500 text-sm">No posts yet.</p>}
        </div>
      </div>
    </div>
  );
}

function timeAgo(iso) {
  try {
    const d = (Date.now() - new Date(iso).getTime()) / 1000;
    if (d < 60) return "just now";
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    return `${Math.floor(d / 86400)}d ago`;
  } catch { return ""; }
}

function Activity({ onRead }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/community/notifications").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (data && data.unread > 0) {
      api.post("/community/notifications/read").then(() => onRead && onRead());
    }
  }, [data, onRead]);
  if (!data) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  if (data.items.length === 0) return <p className="py-16 text-center text-zinc-500">No activity yet. When barbers support your posts, you'll see it here.</p>;
  return (
    <div data-testid="activity-feed" className="max-w-2xl divide-y divide-white/10 rounded-xl border border-white/10">
      {data.items.map((n) => (
        <div key={n.id} data-testid={`notif-${n.id}`} className={`flex items-center gap-3 px-4 py-3 ${n.read ? "" : "bg-[#DCA846]/5"}`}>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#DCA846]/30 text-[#DCA846]"><Heart className="h-4 w-4" /></div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-zinc-200">{n.message}</p>
            <p className="text-xs text-zinc-500">{timeAgo(n.created_at)}</p>
          </div>
          {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-[#DCA846]" />}
        </div>
      ))}
    </div>
  );
}

function Membership({ me, onChange }) {
  const [busy, setBusy] = useState(false);
  const m = me?.membership;
  if (!m) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;

  const subscribe = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/community/membership/subscribe", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setBusy(false); }
  };
  const cancel = async () => {
    if (!confirm("Cancel your membership? You keep access until the end of your paid period.")) return;
    setBusy(true);
    try { await api.post("/community/membership/cancel"); toast.success("Membership set to cancel at period end."); onChange && onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
    finally { setBusy(false); }
  };
  const nextBilling = m.next_billing ? new Date(m.next_billing).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : null;

  return (
    <div data-testid="membership-panel" className="max-w-xl rounded-2xl border border-[#DCA846]/25 bg-gradient-to-br from-[#DCA846]/10 via-black/40 to-transparent p-7">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-full border border-[#DCA846]/30 text-[#DCA846]"><CreditCard className="h-5 w-5" /></div>
        <div>
          <h2 className="font-display text-xl uppercase tracking-wide text-white">Community Membership</h2>
          <p className="text-sm text-zinc-400">One account for your courses and the community.</p>
        </div>
      </div>

      {!m.paid_required ? (
        <div className="mt-6 flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-400" />
          <span data-testid="membership-status" className="text-sm text-emerald-300">Community access is <b>FREE</b> for all members right now. Enjoy!</span>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black/40 px-4 py-3">
            <span className="text-sm text-zinc-400">Status</span>
            <span data-testid="membership-status" className={`font-display uppercase ${m.access ? "text-emerald-400" : "text-red-400"}`}>
              {m.status}{m.cancel_at_period_end ? " (cancels at period end)" : ""}
            </span>
          </div>
          {nextBilling && (
            <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black/40 px-4 py-3">
              <span className="text-sm text-zinc-400">{m.cancel_at_period_end ? "Access until" : "Next billing"}</span>
              <span data-testid="next-billing" className="text-white">{nextBilling}</span>
            </div>
          )}
          <div className="flex items-baseline gap-2">
            <span className="font-display text-3xl text-[#DCA846]">${Number(m.price).toFixed(2)}</span>
            <span className="text-sm uppercase text-zinc-400">/ month</span>
          </div>
          {m.access && m.status === "active" && !m.cancel_at_period_end ? (
            <Button data-testid="cancel-membership-btn" onClick={cancel} disabled={busy} variant="outline" className="border-red-500/40 text-red-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><XCircle className="mr-1 h-4 w-4" /> Cancel Membership</>)}
            </Button>
          ) : m.cancel_at_period_end ? (
            <Button data-testid="resubscribe-btn" onClick={subscribe} disabled={busy} className="bg-[#DCA846] text-black hover:bg-[#C5963B] font-display uppercase">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reactivate Membership"}
            </Button>
          ) : (
            <Button data-testid="subscribe-btn" onClick={subscribe} disabled={busy} className="bg-[#DCA846] text-black hover:bg-[#C5963B] font-display uppercase">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Subscribe — $${Number(m.price).toFixed(2)}/mo`}
            </Button>
          )}
          <p className="text-xs text-zinc-500">Billed monthly via Stripe. Cancel anytime — you keep access until the end of your paid period and won't be charged again. Separate from your course purchases.</p>
        </div>
      )}
    </div>
  );
}

function MyCredits() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/community/ledger").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <Loader2 className="h-8 w-8 animate-spin text-[#DCA846]" />;
  return (
    <div className="max-w-lg divide-y divide-white/10 rounded-xl border border-white/10">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between px-4 py-3 text-sm">
          <span className="text-zinc-300">{r.note}</span>
          <span className={`font-display ${r.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>{r.amount >= 0 ? "+" : ""}{r.amount}</span>
        </div>
      ))}
      {rows.length === 0 && <p className="p-6 text-center text-zinc-500">No credit activity yet.</p>}
    </div>
  );
}
